import React, { useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { Lock } from 'lucide-react'
import {
  useClearSubscriptionOverride,
  useSetSubscriptionOverride,
} from '@data/subscriptions/subscriptions'
import { DeviceAlertStatus } from '@data/subscriptions/model/subscriptions.model'

/** Statuses an admin may pin a device to; mirrors the backend's allowed set. */
const OVERRIDABLE: { value: DeviceAlertStatus; label: string }[] = [
  { value: 'paid', label: 'Paid' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'restricted', label: 'Restricted' },
  { value: 'pending', label: 'Pending 1st Payment' },
]

const STATUS_BG: Record<string, string> = {
  paid: '#1e7e34',
  healthy: '#28a745',
  upcoming: '#f0ad4e',
  overdue: '#dc3545',
  restricted: '#dc3545',
  pending: '#4f5dd6',
  no_subscription: '#6c757d',
}

const STATUS_LABEL: Record<string, string> = {
  paid: 'Paid',
  healthy: 'Healthy',
  upcoming: 'Upcoming',
  overdue: 'Overdue',
  restricted: 'Restricted',
  pending: 'Pending 1st Payment',
  no_subscription: 'No Subscription',
}

interface Props {
  accountId: number
  deviceAccountLinkId: number
  /** The state actually being displayed — the override when one is active. */
  paymentState: string | null
  overrideActive: boolean
  overrideReason: string | null
  /** The Stripe-derived status an override is masking. */
  stripeDerivedStatus: string | null
  /** Admins may change it; everyone else sees a read-only badge. */
  canEdit: boolean
}

/**
 * Renewal status cell for the Device Breakdown.
 *
 * For admins this is an editable select that pins a temporary SMART FLOW
 * status during the Xero-to-Stripe migration (SF-APP-009). Setting one never
 * touches the Stripe subscription — it only changes what SMART FLOW displays,
 * and Stripe reclaims the status as soon as a newer event arrives.
 *
 * When an override is active the masked Stripe status is always shown beside
 * it, so the displayed value can never be mistaken for Stripe's own.
 */
const RenewalStatusSelect: React.FC<Props> = ({
  accountId,
  deviceAccountLinkId,
  paymentState,
  overrideActive,
  overrideReason,
  stripeDerivedStatus,
  canEdit,
}) => {
  const [pendingStatus, setPendingStatus] = useState<DeviceAlertStatus | null>(null)
  const [reason, setReason] = useState('')

  const setOverride = useSetSubscriptionOverride(accountId)
  const clearOverride = useClearSubscriptionOverride(accountId)

  const current = paymentState ?? 'no_subscription'
  const busy = setOverride.isPending || clearOverride.isPending

  const badge = (
    <span
      style={{
        backgroundColor: STATUS_BG[current] ?? '#6c757d',
        color: '#fff',
        padding: '0.2rem 0.6rem',
        borderRadius: 999,
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
      }}
    >
      {STATUS_LABEL[current] ?? current}
    </span>
  )

  function handleSelect(value: string) {
    if (value === '__clear__') {
      clearOverride.mutate(deviceAccountLinkId)
      return
    }
    // A reason is mandatory, so collect it before sending anything.
    setPendingStatus(value as DeviceAlertStatus)
    setReason('')
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault()
    if (!pendingStatus || !reason.trim()) return
    try {
      await setOverride.mutateAsync({
        device_account_link_id: deviceAccountLinkId,
        status: pendingStatus,
        reason: reason.trim(),
      })
    } catch {
      // Surfaced by the mutation; keep the dialog open so it can be retried.
      return
    }
    setPendingStatus(null)
  }

  if (!canEdit) {
    return (
      <div className="d-flex flex-column gap-1">
        {badge}
        {overrideActive && (
          <span className="text-muted" style={{ fontSize: '0.65rem' }}>
            <Lock size={10} className="me-1" />
            Manual override · Stripe: {STATUS_LABEL[stripeDerivedStatus ?? ''] ?? '—'}
          </span>
        )}
      </div>
    )
  }

  return (
    <>
      <div className="d-flex flex-column gap-1">
        <Form.Select
          size="sm"
          value={current}
          disabled={busy}
          onChange={(e) => handleSelect(e.target.value)}
          style={{ minWidth: 150 }}
          aria-label="Renewal status"
        >
          {/* The current value may be a Stripe-derived state that is not itself
              overridable (e.g. healthy), so it is listed to keep the select
              controlled without offering it as a choice. */}
          {!OVERRIDABLE.some((o) => o.value === current) && (
            <option value={current}>{STATUS_LABEL[current] ?? current}</option>
          )}
          {OVERRIDABLE.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          {overrideActive && <option value="__clear__">Clear override (use Stripe)</option>}
        </Form.Select>
        {overrideActive && (
          <span
            className="text-muted"
            style={{ fontSize: '0.65rem' }}
            title={overrideReason ?? undefined}
          >
            <Lock size={10} className="me-1" />
            Manual · Stripe: {STATUS_LABEL[stripeDerivedStatus ?? ''] ?? '—'}
          </span>
        )}
      </div>

      <Modal show={pendingStatus !== null} onHide={() => setPendingStatus(null)} centered>
        <Form onSubmit={handleConfirm}>
          <Modal.Header closeButton>
            <Modal.Title className="h6">
              Override status to &ldquo;{STATUS_LABEL[pendingStatus ?? ''] ?? ''}&rdquo;
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="small text-muted">
              This changes only what SMART FLOW displays. The Stripe subscription is
              left untouched, and the override is cleared automatically when newer
              Stripe data arrives.
            </p>
            <Form.Group>
              <Form.Label className="small fw-semibold">Migration reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. September renewal billed through Xero"
                maxLength={500}
                required
                autoFocus
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setPendingStatus(null)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!reason.trim() || busy}>
              {busy && <Spinner size="sm" animation="border" className="me-1" />}
              Apply override
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}

export default RenewalStatusSelect
